import { useState, useCallback, useEffect } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import './ImageCarousel.css';

interface CarouselImage {
  readonly url: string;
  readonly label: string;
  // CR-112: optional stable identifier (e.g. screen key) used by
  // per-tile action render-props. Falls back to the label when absent.
  readonly key?: string;
}

interface ImageCarouselProps {
  readonly images: CarouselImage[];
  readonly featureGraphic?: CarouselImage | null;
  // CR-112: optional render prop injecting a per-tile overlay (e.g. the
  // Regenerate button on the theme detail page). The carousel positions
  // the container; the caller controls the contents and styling.
  readonly renderTileOverlay?: (image: CarouselImage) => ReactNode;
  // CR-112: optional callback when a tile <img> fails to load. The
  // theme detail page uses this to surface an amber "generation failed"
  // banner scoped to the failing tile without re-rendering the carousel.
  readonly onTileImageError?: (image: CarouselImage) => void;
  // CR-112: optional per-tile banner (render prop). Drawn on top of the
  // tile overlay when an image has failed to load.
  readonly renderTileBanner?: (image: CarouselImage) => ReactNode;
}

const VISIBLE_COUNT = 3;

export function ImageCarousel({
  images,
  featureGraphic,
  renderTileOverlay,
  onTileImageError,
  renderTileBanner,
}: ImageCarouselProps) {
  const handleImageError = useCallback(
    (image: CarouselImage) => (_: SyntheticEvent<HTMLImageElement>) => {
      onTileImageError?.(image);
    },
    [onTileImageError],
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxImage, setLightboxImage] = useState<CarouselImage | null>(null);

  const totalImages = images.length;

  // Calculate window range
  const windowStart = currentIndex + 1;
  const windowEnd = Math.min(currentIndex + VISIBLE_COUNT, totalImages);

  // Can we go previous/next?
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex + VISIBLE_COUNT < totalImages;

  const goPrevious = useCallback(() => {
    if (canGoPrevious) {
      setCurrentIndex((i) => i - 1);
    }
  }, [canGoPrevious]);

  const goNext = useCallback(() => {
    if (canGoNext) {
      setCurrentIndex((i) => i + 1);
    }
  }, [canGoNext]);

  // Lightbox handlers
  const openLightbox = useCallback((image: CarouselImage) => {
    setLightboxImage(image);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxImage(null);
  }, []);

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && lightboxImage) {
        closeLightbox();
      }
    };

    if (lightboxImage) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when lightbox is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxImage, closeLightbox]);

  // Don't render if no images and no feature graphic
  if (totalImages === 0 && !featureGraphic) return null;

  return (
    <div className="image-carousel" data-testid="image-carousel">
      {/* Main Carousel */}
      {totalImages > 0 && (
        <div className="carousel__wrapper">
          {/* Previous Arrow */}
          {canGoPrevious && (
            <button
              className="carousel__arrow carousel__arrow--left"
              onClick={goPrevious}
              aria-label="Previous"
            >
              <ChevronLeft size={24} />
            </button>
          )}

          {/* Viewport showing 3 images */}
          <div className="carousel__viewport" data-testid="carousel-viewport">
            <div 
              className="carousel__track"
              style={{ transform: `translateX(-${currentIndex * (100 / VISIBLE_COUNT)}%)` }}
            >
              {images.map((image, index) => (
                <div
                  key={`${image.label}-${index}`}
                  className="carousel__slide"
                >
                  <div
                    className="carousel__phone-frame"
                    onClick={() => openLightbox(image)}
                    style={{ cursor: 'pointer', position: 'relative' }}
                  >
                    <img
                      src={image.url}
                      alt={image.label}
                      className="carousel__image"
                      onError={handleImageError(image)}
                    />
                    {renderTileOverlay ? (
                      <div
                        className="carousel__tile-overlay"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {renderTileOverlay(image)}
                      </div>
                    ) : null}
                    {renderTileBanner ? (
                      <div
                        className="carousel__tile-banner-wrapper"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {renderTileBanner(image)}
                      </div>
                    ) : null}
                  </div>
                  <span className="carousel__image-label">{image.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Next Arrow */}
          {canGoNext && (
            <button
              className="carousel__arrow carousel__arrow--right"
              onClick={goNext}
              aria-label="Next"
            >
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      )}

      {/* Window Counter */}
      {totalImages > 0 && (
        <div className="carousel__counter">
          <span className="carousel__counter-text">
            {windowStart} - {windowEnd} of {totalImages}
          </span>
        </div>
      )}

      {/* Feature Graphic */}
      {featureGraphic && (
        <div className="carousel__feature">
          <h4 className="carousel__feature-title">Feature Graphic</h4>
          <div
            className="carousel__feature-frame"
            onClick={() => openLightbox(featureGraphic)}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <img
              src={featureGraphic.url}
              alt={featureGraphic.label}
              className="carousel__feature-image"
              onError={handleImageError(featureGraphic)}
            />
            {renderTileOverlay ? (
              <div
                className="carousel__tile-overlay"
                onClick={(e) => e.stopPropagation()}
              >
                {renderTileOverlay(featureGraphic)}
              </div>
            ) : null}
            {renderTileBanner ? (
              <div
                className="carousel__tile-banner-wrapper"
                onClick={(e) => e.stopPropagation()}
              >
                {renderTileBanner(featureGraphic)}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Lightbox Overlay */}
      {lightboxImage && (
        <div 
          className="lightbox__overlay"
          data-testid="lightbox-overlay"
          onClick={closeLightbox}
        >
          <div 
            className="lightbox__backdrop"
            data-testid="lightbox-backdrop"
          />
          <div className="lightbox__content">
            <button
              className="lightbox__close"
              onClick={closeLightbox}
              aria-label="Close lightbox"
            >
              <X size={24} />
            </button>
            <div className="lightbox__image-container">
              <img
                src={lightboxImage.url}
                alt={lightboxImage.label}
                className="lightbox__image"
                data-testid="lightbox-image"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="lightbox__label" data-testid="lightbox-label">
              {lightboxImage.label}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
